import { useMemo, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';
import { ChevronDown } from 'lucide-react';

import { useNavigateWithTransition } from '@muzly/shared';

import { toRecentSummary } from './recentSummary';
import {
  MIN_TREND_POINTS,
  TREND_AXES,
  TREND_DAYS,
  TREND_WINDOWS,
  axisValue,
  buildTrendSeries,
} from './trendSeries';
import { ScoreHero, SectionHeadCard } from '../../components';

import type {
  TrendAxis,
  TrendScale,
  TrendSeries,
  TrendWindow,
} from './trendSeries';
import type { DiagnosisRecord } from '../onboarding/record';

/**
 * 최근 보컬 추세(시안 `1014:9946` §7.1) — 종합 점수 하나의 꺾은선 + 선 아래 연분홍 면적.
 * 손수 SVG(차트 의존성 0 — 플랜 Boundaries).
 *
 * 축은 넷이다 — 종합·호흡 안정도·음역·음정 정확도(시안 20260730 §7.2 `1019:34894`).
 * 값은 전부 `buildTrendSeries` 한 곳에서 나온다. 음역만 자와 단위가 다르다(반음).
 *
 * 결측일은 **끊는다**. 0으로 이으면 안 잰 날이 "0점이던 날"이 되고, 그건 저장 계층이 지킨
 * "없으면 필드가 없다" 규약을 화면에서 뒤집는 것이다. 면적도 구간마다 따로 닫는다.
 */

interface TrendChartProps {
  /** 저장소가 내는 그대로. 이 컴포넌트는 저장소를 읽지 않는다(홈이 한 번만 읽는다). */
  records: DiagnosisRecord[];
  /** epoch ms — 창의 오른쪽 끝. 호출자가 준다(셀렉터와 같은 규약). */
  now: number;
}

const W = 339;
const H = 172;
/** 시안 `1016:10419` — 왼쪽 눈금 글자 22px + 간격 20px, 위 8px, 아래 날짜 줄 24px.
 *  오른쪽 여백은 눈금이 아니라 **마지막 날짜 글자**가 정한다 — 가운데 정렬이라
 *  절반이 마지막 점 오른쪽으로 나가고, viewBox를 넘으면 SVG가 그걸 잘라낸다. */
const PAD = { l: 42, r: 15, t: 8, b: 24 };

/** 칸 수는 창 길이가 정한다 — 7일과 30일이 같은 폭을 나눠 쓴다. */
/**
 * 가로 눈금 셋 — 시안 `1016:10420`의 100 · 50 · 0. 촘촘한 `scale.ticks` 전부를 그리면
 * 칸이 빽빽해져 선이 안 보인다.
 *
 * **자에서 뽑는다.** 예전에는 `[100, 50, 0]` 이 상수였는데, 음역 축은 0~48반음이라
 * 축 토글이 생긴 날 48짜리 눈금판에 100이 찍혔다 — 격자가 값을 거짓말한다.
 */
const gridTicks = (scale: TrendScale): number[] => {
  const { ticks } = scale;
  return [
    ticks[ticks.length - 1] ?? scale.max,
    ticks[Math.floor((ticks.length - 1) / 2)] ?? scale.max / 2,
    ticks[0] ?? 0,
  ];
};

const xAt = (i: number, count: number): number =>
  PAD.l + (i * (W - PAD.l - PAD.r)) / Math.max(1, count - 1);

/** 눈금 밖 값도 그림 안에 머문다 — 텍스트 대안은 클램프 전 원값을 읽는다. */
const yAt = (val: number, max: number): number => {
  const clamped = Math.min(Math.max(val, 0), max);
  return PAD.t + (1 - clamped / max) * (H - PAD.t - PAD.b);
};

/** 날짜 라벨을 찍을 칸인가 — 시안은 양 끝과 가운데 **셋**만 찍는다(`08.01 · 08.12 · 08.19`). */
const labelVisible = (index: number, count: number): boolean =>
  index === 0 || index === count - 1 || index === Math.floor((count - 1) / 2);

/**
 * 잰 날들 — 시안(`1016:10419`)은 회차와 회차를 **선으로 잇는다**(`08.01 → 08.12 → 08.19`).
 * 사이에 안 잰 날이 있어도 끊지 않는다: x축이 날짜 비례라 점 사이 간격이 "그동안 안
 * 쟀다"를 이미 말하고, 0으로 채우지도 않는다(결측일에 점이 없다).
 */
const measured = (series: TrendSeries): { i: number; val: number }[] =>
  series.points.flatMap((point, i) =>
    point.val === null ? [] : [{ i, val: point.val }],
  );

const pt = (i: number, val: number, series: TrendSeries): string =>
  `${xAt(i, series.points.length).toFixed(1)} ${yAt(val, series.scale.max).toFixed(1)}`;

/** 선과 면적 — 잰 점들을 한 번에 잇고, 면적은 0선까지 내려 `Z`로 닫는다. */
const buildPaths = (series: TrendSeries): { line: string; area: string } => {
  const base = yAt(0, series.scale.max).toFixed(1);
  const x = (i: number) => xAt(i, series.points.length).toFixed(1);
  const run = measured(series);
  if (run.length === 0) {
    return { line: '', area: '' };
  }
  const top = run.map(({ i, val }) => `L${pt(i, val, series)}`).join(' ');
  return {
    line: run
      .map(({ i, val }, k) => `${k ? 'L' : 'M'}${pt(i, val, series)}`)
      .join(' '),
    area: `M${x(run[0].i)} ${base} ${top} L${x(run[run.length - 1].i)} ${base} Z`,
  };
};

const Plot = ({
  series,
  alt,
  onPick,
}: {
  series: TrendSeries;
  alt: string;
  /** 점을 누르면 그 회차를 연다 — 회차를 시간축에 그리는 유일한 화면이라 여기가 입구다. */
  onPick: (recordId: string) => void;
}) => {
  const paths = buildPaths(series);
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="mt-6 w-full"
      role="img"
      aria-label={alt}
    >
      {/* 눈금은 시안대로 100 · 50 · 0 셋 — 12px 글자, BK_600, 얇은 회색 줄. */}
      {gridTicks(series.scale).map((tick) => (
        <g key={tick}>
          <line
            x1={PAD.l}
            x2={W - PAD.r}
            y1={yAt(tick, series.scale.max)}
            y2={yAt(tick, series.scale.max)}
            stroke="var(--bk-100)"
            strokeWidth={1}
          />
          <text
            x={PAD.l - 20}
            y={yAt(tick, series.scale.max) + 4}
            textAnchor="end"
            fontSize={12}
            fill="var(--bk-600)"
          >
            {tick}
          </text>
        </g>
      ))}

      {/* 면적이 선보다 먼저다 — SVG는 그린 순서대로 쌓인다. 연분홍 면(시안 8%). */}
      <path
        data-testid="trend-area"
        d={paths.area}
        fill="hsl(var(--primary) / 0.08)"
      />
      <path
        data-testid="trend-line"
        d={paths.line}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* 점은 선이 아니라 포인트에서 나온다 — 앞뒤가 다 빈 하루도 마커는 찍힌다. 8px 원. */}
      {series.points.map(
        (point, i) =>
          point.val !== null && (
            <circle
              key={point.dayStart}
              cx={xAt(i, series.points.length)}
              cy={yAt(point.val, series.scale.max)}
              // 손끝이 닿는 반경은 눈에 보이는 4px보다 커야 한다 — 보이는 원은 그대로
              // 두고 투명 테두리로만 넓힌다.
              r={4}
              strokeWidth={14}
              stroke="transparent"
              fill="hsl(var(--primary))"
              {...(point.recordId
                ? {
                    role: 'button',
                    tabIndex: 0,
                    className: 'cursor-pointer outline-none',
                    onClick: () => onPick(point.recordId as string),
                    onKeyDown: (e: ReactKeyboardEvent<SVGCircleElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onPick(point.recordId as string);
                      }
                    },
                  }
                : {})}
            />
          ),
      )}

      {/* 30일 창에서는 날짜를 다 찍으면 글자가 겹쳐 뭉갠다 — 일정 간격으로 솎되 양 끝은
        언제나 남긴다. 축이 어디서 시작해 어디서 끝나는지가 안 읽히면 창을 바꾼 것이
        화면에 안 보인다. */}
      {series.points.map((point, i) =>
        labelVisible(i, series.points.length) ? (
          <text
            key={point.dayStart}
            x={xAt(i, series.points.length)}
            y={H - 4}
            textAnchor="middle"
            fontSize={12}
            fill="var(--bk-600)"
          >
            {point.label}
          </text>
        ) : null,
      )}
    </svg>
  );
};

export const TrendChart = ({ records, now }: TrendChartProps) => {
  const { t } = useTranslation();
  const navigate = useNavigateWithTransition();
  const [windowDays, setWindowDays] = useState<TrendWindow>(TREND_DAYS);
  /**
   * 축 토글(시안 20260730 §7.2 `1019:34894`) — 종합 · 호흡 안정도 · 음역 · 음정 정확도.
   *
   * 셀렉터(`trendSeries`)는 처음부터 네 축을 다 낼 수 있었는데 화면이 `'overall'` 하나로
   * 고정돼 있었다. 종합은 스스로 "가중치 미보정"이라고 적어 둔 합성값이라, 그 하나만
   * 그리면 설문이 파는 `꾸준한 성장 트래킹`이 실제로는 보정 안 된 숫자 한 줄이었다.
   */
  const [axis, setAxis] = useState<TrendAxis>('overall');
  const series = useMemo(
    () => buildTrendSeries({ records, axis, now, windowDays }),
    [records, axis, now, windowDays],
  );

  /**
   * 시안 §7.1의 `종합 점수 78 / 100`과 `4점 향상` 배지.
   *
   * 향상 폭은 **창 안의 첫 값과 마지막 값**의 차다 — 직전 회차 대비가 아니다. 이 카드가
   * 말하는 것이 "요즘 어떤가"라서, 하루 못한 회차 때문에 `-9점`이 뜨면 카드 제목과 숫자가
   * 서로 다른 이야기를 한다. 값이 하나뿐이면 견줄 것이 없어 배지를 안 그린다.
   */
  const { first, latest } = series;
  const delta =
    first && latest && series.filled >= MIN_TREND_POINTS
      ? latest.val - first.val
      : null;

  /**
   * 큰 점수는 **마지막 회차**에서 온다 — 추세 창에서가 아니다. 창을 7일로 두고 8일 만에
   * 열면 `latest`가 비어 점수가 화면에서 통째로 사라졌는데, 바로 아래 타일은 그 회차의
   * 값을 계속 보여 줬다(2026-09-16 실기기). 창은 선(추세)만 정한다.
   */
  const lastValue = useMemo(() => {
    const newest = records[0];
    if (!newest) {
      return null;
    }
    return axis === 'overall'
      ? (toRecentSummary(newest)?.overall ?? null)
      : axisValue(newest, axis);
  }, [records, axis]);

  /**
   * 음역 축만 단위가 점이 아니라 **반음**이다. 이 판정을 숫자 내는 자리마다 다시 쓰면
   * 한 군데가 샌다 — 큰 숫자는 `29반음`인데 바로 아래 배지가 `2점 향상`이라고 말했다
   * (2026-09-16 시뮬레이터). 값 문구·향상 폭·낭독 대안이 전부 이 한 줄에서 갈린다.
   */
  const isSemitoneAxis = axis === 'range';

  const label = t(`web.home.trend.axis.${axis}`);
  const alt = latest
    ? t('web.home.trend.alt', {
        axis: label,
        days: windowDays,
        date: latest.label,
        value: t(
          isSemitoneAxis ? 'web.home.trend.semitones' : 'web.home.recent.score',
          { value: Math.round(latest.val) },
        ),
      })
    : t('web.home.trend.altEmpty', { axis: label, days: windowDays });

  return (
    <section className="mt-8">
      <SectionHeadCard
        title={t('web.home.trend.section')}
        right={
          // 시안의 `최근 7일 ∨`. 고를 것이 있으므로 진짜 셀렉트다 — 글자와 화살표만 남기고
          // 브라우저 크롬은 지운다.
          <label className="relative flex min-h-[44px] items-center text-[15px] tracking-[-0.075px] text-bk-600">
            <span className="sr-only">{t('web.home.trend.periodLabel')}</span>
            <select
              value={windowDays}
              onChange={(event) =>
                setWindowDays(Number(event.target.value) as TrendWindow)
              }
              className="appearance-none bg-transparent pr-6 text-right text-[15px] text-bk-600 outline-none"
            >
              {TREND_WINDOWS.map((days) => (
                <option key={days} value={days}>
                  {t('web.home.trend.period', { value: days })}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-0 h-5 w-5 text-bk-600"
              aria-hidden="true"
            />
          </label>
        }
      />

      {/* 축 토글 — 시안 §7.2 의 네 칸. 선택지가 넷뿐이라 스크롤도 드롭다운도 아니다. */}
      <div
        role="tablist"
        aria-label={t('web.home.trend.axisLabel')}
        className="mt-4 flex gap-1 overflow-x-auto px-[18px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {TREND_AXES.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={id === axis}
            onClick={() => setAxis(id)}
            // 결과 탭과 같은 줄 — 균등 분할(`flex-1`)로 두면 `호흡 안정도`가 390px 에서
            // `호흡 안정 / 도` 로 꺾인다. 칸은 글자가 정하고 모자라면 옆으로 흐른다.
            className={cn(
              'min-h-[44px] shrink-0 whitespace-nowrap rounded-full px-3 text-[13px] font-medium tracking-[-0.065px] transition-colors',
              id === axis
                ? 'bg-primary/5 font-semibold text-primary'
                : 'text-bk-600',
            )}
          >
            {t(`web.home.trend.axisShort.${id}`)}
          </button>
        ))}
      </div>

      {/* 선택한 축의 최근 값 + 향상 폭(시안 §7.1) — 카드 밖 `#fafafa` 위. 숫자 위에 축 이름을
          세운다 — 숫자만 두면 78이 무엇의 78인지 알 수 없다. */}
      {lastValue !== null && (
        <div className="mt-4 flex flex-col gap-3.5 px-[18px] py-4">
          <p className="text-sm tracking-[-0.07px] text-bk-600">{label}</p>
          {/* 음역은 점수가 아니라 **반음 폭**이라 `/100` 자를 못 쓴다 — 붙이면 32반음이
              32점으로 읽힌다(TREND_SCALE 주석과 같은 이유). */}
          {isSemitoneAxis ? (
            <p className="text-[40px] font-bold leading-none tracking-[-0.2px] text-primary">
              {t('web.home.trend.semitones', {
                value: Math.round(lastValue),
              })}
            </p>
          ) : (
            <ScoreHero score={lastValue} tone="primary" />
          )}
          {/* 종합 점수가 어떻게 만들어졌는지 — 제품이 "미보정"이라고 말하기로 한 자리다
              (PRODUCT.md 원칙 2). 합성값에만 붙는 단서라 다른 축에서는 뺀다. */}
          {axis === 'overall' && (
            <p className="text-xs leading-relaxed text-bk-500">
              {t('web.home.recent.uncalibrated')}
            </p>
          )}
          {delta !== null && delta !== 0 && (
            <span
              data-testid="trend-delta"
              className={cn(
                'inline-block self-start rounded-full px-3 py-1.5 text-[13px] font-semibold tracking-[-0.065px]',
                delta > 0
                  ? 'bg-[rgba(255,45,85,0.08)] text-pink'
                  : 'bg-bk-50 text-bk-600',
              )}
            >
              {t(
                `web.home.trend.delta${delta > 0 ? 'Up' : 'Down'}${
                  isSemitoneAxis ? 'Semitones' : ''
                }`,
                { value: Math.abs(Math.round(delta)) },
              )}
            </span>
          )}
        </div>
      )}

      {series.filled < MIN_TREND_POINTS ? (
        <p className="mt-6 text-center text-sm text-bk-600">
          {t('web.home.trend.empty')}
        </p>
      ) : (
        <Plot
          series={series}
          alt={alt}
          onPick={(recordId) => navigate(`/result/${recordId}`)}
        />
      )}
    </section>
  );
};
